import React, { useRef, useEffect } from 'react';
import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion';
import { FaGithub, FaExternalLinkAlt, FaRocket } from 'react-icons/fa';

const Projects = () => {
  const sectionRef = useRef(null);
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  
  const rotateX = useSpring(useTransform(mouseY, [-300, 300], [5, -5]));
  const rotateY = useSpring(useTransform(mouseX, [-300, 300], [-5, 5]));
  
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (sectionRef.current) {
        const rect = sectionRef.current.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;
        mouseX.set(e.clientX - centerX);
        mouseY.set(e.clientY - centerY);
      }
    };
    
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [mouseX, mouseY]);

  const projects = [
    {
      title: "Gig-Based Social Platform",
      description: "Full-stack social networking platform connecting freelancers with employers for project-based work",
      tech: ["React.js", "Material UI", "Node.js", "Express.js", "Mongoose"],
      github: "#",
      live: "#",
      color: "cyan"
    },
    {
      title: "Transportation Management System",
      description: "Comprehensive logistics platform for managing trucks, drivers, and shipments with real-time tracking",
      tech: ["React.js", "TypeScript", "Ruby on Rails", "PostgreSQL", "Docker"],
      github: "#",
      live: "#",
      color: "purple"
    },
    {
      title: "Mosque Management App",
      description: "Mobile application for mosque administration with community engagement and real-time notifications",
      tech: ["React Native", "React.js", "Node.js", "Firebase", "FCM"],
      github: "#",
      live: "#",
      color: "green"
    }
  ];

  return (
    <section id="projects" className="projects-futuristic" ref={sectionRef}>
      <div className="quantum-field-bg"></div>
      <div className="container">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
          className="section-header-futuristic"
          style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
        >
          <motion.h2 
            className="section-title-futuristic"
            whileHover={{ scale: 1.05, textShadow: "0 0 30px #00f5ff" }}
          >
            Projects
          </motion.h2>
          <motion.p 
            className="section-subtitle-futuristic"
            whileHover={{ scale: 1.02 }}
          >
            3+ years of building scalable web and mobile applications
          </motion.p>
        </motion.div>
        
        <div className="projects-grid-futuristic">
          {projects.map((project, index) => (
            <motion.div
              key={index}
              className={`project-card-futuristic ${project.color}`}
              initial={{ opacity: 0, y: 100, rotateX: -30 }}
              whileInView={{ opacity: 1, y: 0, rotateX: 0 }}
              transition={{ duration: 0.8, delay: index * 0.2 }}
              whileHover={{ 
                scale: 1.05,
                rotateY: 10,
                z: 50,
                boxShadow: `0 30px 60px rgba(0, 245, 255, 0.4)`
              }}
            >
              <div className="project-glow"></div>
              <div className="project-content">
                <motion.h3 
                  className="project-title"
                  whileHover={{ scale: 1.1, color: "#00f5ff" }}
                >
                  {project.title}
                </motion.h3>
                <p className="project-description">{project.description}</p>
                
                <div className="tech-stack">
                  {project.tech.map((tech, techIndex) => (
                    <motion.span
                      key={techIndex}
                      className="tech-tag"
                      whileHover={{ 
                        scale: 1.2, 
                        rotate: 5,
                        boxShadow: "0 0 15px rgba(0, 245, 255, 0.6)"
                      }}
                    >
                      {tech}
                    </motion.span>
                  ))}
                </div>
                
                <div className="project-links">
                  <motion.a
                    href={project.github}
                    className="project-link github"
                    whileHover={{ scale: 1.2, rotate: 360 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <FaGithub />
                  </motion.a>
                  <motion.a
                    href={project.live}
                    className="project-link live"
                    whileHover={{ scale: 1.2, rotate: -360 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <FaExternalLinkAlt />
                  </motion.a>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Projects;